import { useMutation, useQuery } from "@tanstack/react-query";
import {
  FINANCE_MODULE_ID,
  LEARN_VERTICAL_ID,
  SCA_ONE_APP_MIGRATED,
  SCA_VERTICAL_ID,
} from "constants/constants";
import { DAY, QUERY_RETRY_ATTEMPTS } from "constants/queryCacheTimes";
import {
  getCountryIdFromUrl,
  getIsdCodeFromUrl,
  getOneOmsBaseUrl,
  getSearchUserUrl,
  getUploadServiceBaseUrl,
} from "constants/omsRegionConfig";
import Cookies from "js-cookie";
import { toISTDateTime } from "utils/formatDate";

const uploadCsv = async ({ file, selectedScriptId, uploaderUserId, region }) => {
  const url = new URL(
    `${getUploadServiceBaseUrl(region)}/upload-file?uploaded_by=${uploaderUserId}&selected_script_id=${selectedScriptId}`,
  );

  const headers = {
    accept: "application/json",
    "secret-key": `${process.env.REACT_APP_UPLOAD_SERVICE_SECRET_KEY}`,
  };

  const formData = new FormData();
  formData.append("file", file);

  const response = await fetch(url, {
    method: "POST",
    headers,
    body: formData,
  });

  if (!response.ok) {
    const errorData = await response.json();
    throw new Error(
      errorData.message || `Failed to upload file. Status: ${response.status}`,
    );
  }

  const data = await response.json();

  if (data.status === "success") {
    return data.data;
  } else {
    throw new Error(
      data.message || "An error occurred while discarding the order.",
    );
  }
};

export const useUploadCsv = () => {
  return useMutation({ mutationFn: uploadCsv });
};

const fetchHistory = async (user_id, limit = 10, offset = 0, region) => {
  const url = new URL(
    `${getUploadServiceBaseUrl(region)}/get-all-files?uploader_user_id=${user_id}&module_id=${FINANCE_MODULE_ID}&offset=${offset}&limit=${limit}`,
  );

  const headers = {
    accept: "application/json",
    "secret-key": process.env.REACT_APP_UPLOAD_SERVICE_SECRET_KEY,
    "Content-Type": "application/json",
  };

  const response = await fetch(url, {
    method: "GET",
    headers: headers,
    body: null,
  });

  if (!response.ok) {
    const errorData = await response.json();
    throw new Error(
      errorData.message ||
        `Failed to fetch history. Status: ${response.status}`,
    );
  }

  const data = await response.json();

  if (data.status === "success") {
    return data.data;
  } else {
    throw new Error(
      data.message || "An error occurred while fetching pending orders.",
    );
  }
};

export const useFetchHistory = (user_id, limit = 10, offset = 0, region) => {
  return useQuery({
    queryKey: ["history", user_id, limit, offset, region],
    queryFn: () => fetchHistory(user_id, limit, offset, region),
    enabled: Boolean(region),
  });
};

const fetchProcessedFile = async (fileId, region) => {
  const url = new URL(
    `${getUploadServiceBaseUrl(region)}/download-processed-file/${fileId}`,
  );

  const headers = {
    accept: "application/json",
    "secret-key": process.env.REACT_APP_UPLOAD_SERVICE_SECRET_KEY,
    "Content-Type": "application/json",
  };

  const response = await fetch(url, {
    method: "GET",
    headers: headers,
  });

  if (!response.ok) {
    const errorData = await response.json();
    throw new Error(
      errorData.message ||
        `Failed to fetch processed file. Status: ${response.status}`,
    );
  }

  const data = await response.json();

  if (data.status === "success") {
    return { signedUrl: data.data.signed_url };
  } else {
    throw new Error(
      data.message || "An error occurred while fetching the processed file.",
    );
  }
};

export const useFetchProcessedFile = (region) => {
  return useMutation({
    mutationFn: (fileId) => fetchProcessedFile(fileId, region),
  });
};

const extractUserDetails = (globalUsers, selectedBusinessVertical) => {
  if (!globalUsers || !Array.isArray(globalUsers.accounts)) return [];

  if (!localStorage.getItem("cashierPhoneNumber")) {
    throw new Error(
      "Oops! Something doesn't look right. Please log out and log in again to continue.",
    );
  }

  const isSameUser = globalUsers.accounts.some(
    (account) => account.phone === localStorage.getItem("cashierPhoneNumber"),
  );

  if (isSameUser) {
    throw new Error(
      "Seems like you are creating an order for yourself. Please select a different user to create an order.",
    );
  }

  const filteredUsersByVertical = globalUsers.accounts.flatMap((account) =>
    account.profiles
      .filter(
        (profile) =>
          Number(profile.vertical_id) === Number(selectedBusinessVertical),
      )
      .map((profile) => ({
        firstName: account.first_name,
        lastName: account.last_name,
        phone: account.phone,
        verticalId: profile.vertical_id,
        uamId: profile.uam_id,
      })),
  );

  if (filteredUsersByVertical.length === 0) {
    throw new Error("No user found for the selected business vertical.");
  }

  return filteredUsersByVertical;
};

const fetchCustomerInfoFromOneApp = async (payload) => {

  const url = getSearchUserUrl({
    isdCode: payload.isdCode,
    countryId: payload.countryId,
    countryCode: payload.countryCode,
  });

  const authToken = Cookies.get("jweToken");

  const requestPayload = {
    isd_code: payload.isdCode,
    phone: payload.phoneNumber,
  };

  const headers = {
    Accept: "application/json",
    "Content-Type": "application/json",
    "product-id": "100",
    "tenant-id": "99999",
    platform: "web",
    Authorization: `Bearer ${authToken}`,
  };

  const response = await fetch(url, {
    method: "POST",
    headers,
    body: JSON.stringify(requestPayload),
  });

  if (response.status === 401) {
    throw new Error("Unauthorized: Please login again.");
  }

  if (response.status === 403) {
    throw new Error("Forbidden: You don't have access to this resource.");
  }
  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(
      `Failed to fetch customer info: ${response.status} ${errorText}`,
    );
  }

  const responseData = await response.json();

  return extractUserDetails(responseData, payload.selectedBusinessVertical);
};

const formatKHUserToUserFormat = (users) => {
  const formattedUsers = users.map((account) => {
    return ({
      firstName: account.first_name,
      lastName: account.last_name,
      phone: account.phone,
      verticalId: SCA_VERTICAL_ID,
      uamId: account.uam_id,
    })
  });
  return formattedUsers;
};
const fetchKHCustomerInfo = async (payload) => {
  const url = new URL(process.env.REACT_APP_GET_VERIFY_USER);

  const authToken = Cookies.get("jweToken");

  const headers = {
    accept: "application/json",
    "product-id": "900",
    platform: "web",
    "tenant-id": "7",
    Authorization: `Bearer ${authToken}`,
    "Content-Type": "application/json",
  };

  const requestPayload = {
    isd_code: payload.isdCode,
    phone: payload.phoneNumber,
  };

  const response = await fetch(url, {
    method: "POST",
    headers,
    body: JSON.stringify(requestPayload),
  });

  if (response.status === 401) {
    throw new Error("Unauthorized: Please login again.");
  }

  if (response.status === 403) {
    throw new Error("Forbidden: You don't have access to this resource.");
  }

  if (!response.ok) {
    throw new Error(
      "Failed to fetch KH customer info, please try again later.",
    );
  }

  const data = await response.json();

  return formatKHUserToUserFormat(data.users);
};

const fetchCustomerInfoSeparately = async (payload) => {
  if (Number(payload.selectedBusinessVertical) === Number(LEARN_VERTICAL_ID)) {
    return fetchCustomerInfoFromOneApp(payload);
  }

  return fetchKHCustomerInfo(payload);
};

const fetchCustomerInfo = async (payload) => {
  return SCA_ONE_APP_MIGRATED === true
    ? fetchCustomerInfoFromOneApp(payload)
    : fetchCustomerInfoSeparately(payload);
};

export const useFetchCustomerInfo = () => {
  return useMutation({ mutationFn: fetchCustomerInfo });
};

const fetchCustomerOrderDetails = async (customerId) => {
  const url = new URL(
    `${getOneOmsBaseUrl()}/fetch-customer-orders-details`,
  );

  const headers = {
    accept: "application/json",
    "secret-key": `${process.env.REACT_APP_ONE_OMS_SECRET_KEY}`,
    "Content-Type": "application/json",
  };

  const payload = { uam_id: customerId };

  const response = await fetch(url, {
    method: "POST",
    headers: headers,
    body: JSON.stringify(payload),
  });

  const data = await response.json();

  if (data.status && data.status === "success") {
    return data.data.order_details;
  } else {
    throw new Error(
      data.message || "An error occurred while processing the request.",
    );
  }
};

export const useFetchCustomerOrderDetails = (customerId) => {
  const countryId = getCountryIdFromUrl();
  const isdCode = getIsdCodeFromUrl();
  return useQuery({
    queryKey: ["customer_order_details", customerId, countryId, isdCode],
    queryFn: () => fetchCustomerOrderDetails(customerId),
    enabled: !!customerId,
  });
};

const fetchOrderDetails = async (orderId) => {
  const url = new URL(
    `${getOneOmsBaseUrl()}/get-order-info/${orderId}`,
  );

  const headers = {
    accept: "application/json",
    "secret-key": `${process.env.REACT_APP_ONE_OMS_SECRET_KEY}`,
    "Content-Type": "application/json",
  };

  const response = await fetch(url, { headers });

  const data = await response.json();

  if (data.status && data.status === "success") {
    return data.data.order_details;
  } else {
    throw new Error(
      data.message || "An error occurred while processing the request.",
    );
  }
};

export const useFetchOrderDetails = (orderId) => {
  const countryId = getCountryIdFromUrl();
  const isdCode = getIsdCodeFromUrl();
  return useQuery({
    queryKey: ["order_details", orderId, countryId, isdCode],
    queryFn: () => fetchOrderDetails(orderId),
    enabled: !!orderId,
  });
};

const updateChequeStatus = async (payload) => {
  const url = new URL(
    `${getOneOmsBaseUrl()}/update-cheque-status`,
  );
  const headers = {
    accept: "application/json",
    "secret-key": `${process.env.REACT_APP_ONE_OMS_SECRET_KEY}`,
    "Content-Type": "application/json",
  };
  const response = await fetch(url, {
    method: "POST",
    headers: headers,
    body: JSON.stringify(payload),
  });
  const data = await response.json();
  if (data.status && data.status === "success") {
    return data.data;
  } else {
    throw new Error(
      data?.message ||
        "Oops! Something went wrong. Please refresh the page and try again.",
    );
  }
};

export const useUpdateChequeStatus = () => {
  return useMutation({ mutationFn: updateChequeStatus });
};

const updateBankTransferStatus = async (payload) => {
  const url = new URL(
    `${getOneOmsBaseUrl()}/update-bank-transfer-status`,
  );
  const headers = {
    accept: "application/json",
    "secret-key": `${process.env.REACT_APP_ONE_OMS_SECRET_KEY}`,
    "Content-Type": "application/json",
  };
  const response = await fetch(url, {
    method: "POST",
    headers: headers,
    body: JSON.stringify(payload),
  });
  const data = await response.json();
  if (data.status && data.status === "success") {
    return data.data;
  } else {
    throw new Error(
      data?.message ||
        "Oops! Something went wrong. Please refresh the page and try again.",
    );
  }
};

export const useUpdateBankTransferStatus = () => {
  return useMutation({ mutationFn: updateBankTransferStatus });
};

const banks = async () => {
  const url = new URL(`${getOneOmsBaseUrl()}/get-banks-list`);
  const headers = {
    accept: "application/json",
    "secret-key": `${process.env.REACT_APP_ONE_OMS_SECRET_KEY}`,
    "Content-Type": "application/json",
  };
  const response = await fetch(url, {
    method: "GET",
    headers: headers,
  });
  const data = await response.json();
  if (data.status && data.status === "success") {
    return data.data;
  } else {
    throw new Error(
      data.message || "An error occurred while processing the request.",
    );
  }
};
export const useFetchAllBanks = () => {
  return useQuery({
    queryKey: ["Banks"],
    queryFn: () => banks(),
    staleTime: DAY,
    cacheTime: DAY,
    retry: QUERY_RETRY_ATTEMPTS,
  });
};

const generateOtp = async (phoneNumberAndCaptcha) => {
  const url = new URL(
    `${process.env.REACT_APP_COMMSERVICE_URL}/api/v3/generate-otp`,
  );
  const headers = {
    accept: "application/json",
    "Content-Type": "application/json",
  };
  const payload = {
    isd_code: phoneNumberAndCaptcha.isd_code,
    phone: phoneNumberAndCaptcha.phone,
    tenant_id: 1,
    product_id: 300,
    captcha_token: phoneNumberAndCaptcha.captcha_token,
  };
  const response = await fetch(url, {
    method: "POST",
    headers: headers,
    body: JSON.stringify(payload),
  });
  const data = await response.json();
  if (data.status && data.status === "success") {
    return data.data;
  } else {
    throw new Error(
      data?.message ||
        "Oops! Something went wrong. Please refresh the page and try again.",
    );
  }
};

export const useGenerateOtp = () => {
  return useMutation({ mutationFn: generateOtp });
};

const _morph_user_tenants = (data) => {
  return Object.values(data).map(({ id, name, vertical }) => ({
    value: id,
    label: name,
    verticalId: vertical?.id,
    verticalName: vertical?.name,
  }));
};

export const extractVerticalsFromTenants = (tenants = []) => {
  const verticalMap = new Map();

  tenants.forEach(({ verticalId, verticalName }) => {
    if (verticalId == null || verticalMap.has(verticalId)) return;
    verticalMap.set(verticalId, {
      value: String(verticalId),
      label: verticalName,
    });
  });

  return Array.from(verticalMap.values());
};

export const fetchUserTenants = async (tenantIds) => {
  if (!Array.isArray(tenantIds) || tenantIds.length === 0) {
    throw new Error("No gateway tenant IDs found");
  }
  const url = new URL(
    `${process.env.REACT_APP_PACKAGE_MANAGEMENT_SERVICE_URL}/get-one-oms-tenants-from-gateway-tenants`,
  );

  tenantIds.forEach((id) => {
    url.searchParams.append("gateway_tenant_id", id);
  });

  const response = await fetch(url.toString(), {
    method: "GET",
    headers: {
      accept: "application/json",
      "secret-key": process.env.REACT_APP_PACKAGE_MANAGEMENT_SERVICE_SECRET_KEY,
    },
  });

  const data = await response.json();

  if (!response.ok) {
    throw new Error(data?.message || "Failed to fetch centers");
  }

  return _morph_user_tenants(data.data);
};

export const useFetchUserTenants = () => {
  const tenantIds = JSON.parse(localStorage.getItem("gatewayTenantIds")) || [];

  return useQuery({
    queryKey: ["userTenants"],
    queryFn: () => fetchUserTenants(tenantIds),
  });
};
export const fetchDaySheets = async (filters) => {
  const { centerIds, startDate, endDate, daySheetType, applyFilter } = filters;

  if (!centerIds || !startDate || !endDate || !daySheetType || !applyFilter) {
    throw new Error("Missing required filters to fetch day sheets");
  }

  const url = new URL(`${getOneOmsBaseUrl()}/get-day-sheets`);

  centerIds.forEach((id) => {
    url.searchParams.append("tenant_ids", id);
  });
  url.searchParams.set("day_sheet_close_time_from", toISTDateTime(startDate));
  url.searchParams.set("day_sheet_close_time_to", toISTDateTime(endDate));
  url.searchParams.set("type", daySheetType);

  const headers = {
    accept: "application/json",
    "secret-key": process.env.REACT_APP_ONE_OMS_SECRET_KEY,
  };

  const response = await fetch(url.toString(), {
    method: "GET",
    headers,
  });

  const data = await response.json();

  if (!response.ok) {
    throw new Error(data?.message || `Failed to fetch day sheets.`);
  }

  if (data?.status === "success") {
    return data.data;
  }

  throw new Error(
    data?.message || "An error occurred while fetching day sheets",
  );
};

export const useFetchDaySheets = ({ filters = {} }) => {
  const isEnabled = filters?.applyFilter === true;
  return useQuery({
    queryKey: ["daySheets", filters],
    queryFn: () => fetchDaySheets(filters),
    enabled: !!isEnabled,
  });
};

const downloadDaySheet = async (daySheetId, filters) => {
  const url = new URL(
    `${getOneOmsBaseUrl()}/download-day-sheet`,
  );

  const headers = {
    accept: "application/json",
    "secret-key": process.env.REACT_APP_ONE_OMS_SECRET_KEY,
    "Content-Type": "application/json",
  };

  url.searchParams.set("id", daySheetId);
  url.searchParams.set("type", filters.daySheetType);
  url.searchParams.set("format", filters.format);

  const response = await fetch(url, {
    method: "GET",
    headers: headers,
  });

  if (!response.ok) {
    const errorData = await response.json();
    throw new Error(
      errorData.message ||
        `Failed to fetch processed file. Status: ${response.status}`,
    );
  }

  const data = await response.json();

  if (data.status === "success") {
    return { signedUrl: data.data.signed_url };
  } else {
    throw new Error(
      data.message || "An error occurred while fetching the processed file.",
    );
  }
};

export const useDownloadDaySheet = () => {
  const query = useQuery({
    queryKey: ["downloadDaySheet"],
    queryFn: () => downloadDaySheet(),
    enabled: false,
  });
  return {
    ...query,
    downloadDaySheet: (daySheetId, filters) =>
      downloadDaySheet(daySheetId, filters),
  };
};

const fetchOrderSummaryPDF = async (orderId) => {
  const url = new URL(
    `${getOneOmsBaseUrl()}/get-order-summary/${orderId}`,
  );

  const headers = {
    accept: "application/json",
    "secret-key": `${process.env.REACT_APP_ONE_OMS_SECRET_KEY}`,
    "Content-Type": "application/json",
  };

  const response = await fetch(url, {
    method: "GET",
    headers: headers,
  });

  const data = await response.json();

  if (data.status && data.status === "success") {
    return data.data;
  } else {
    throw new Error(
      data.message || "An error occurred while processing the request.",
    );
  }
};

export const useFetchOrderSummaryPDF = (orderId) => {
  return useQuery({
    queryKey: ["OrderSummary", orderId],
    queryFn: () => fetchOrderSummaryPDF(orderId),
    enabled: false,
    retry: false,
  });
};

const fetchTaxInvoicePDF = async (orderId) => {
  const url = new URL(
    `${getOneOmsBaseUrl()}/get-tax-invoice/${orderId}`,
  );

  const headers = {
    accept: "application/json",
    "secret-key": `${process.env.REACT_APP_ONE_OMS_SECRET_KEY}`,
    "Content-Type": "application/json",
  };

  const response = await fetch(url, {
    method: "GET",
    headers: headers,
  });

  if (!response.ok) {
    const errorData = await response.json();
    throw new Error(errorData.message || `Failed to fetch tax invoice.`);
  }

  const data = await response.json();

  if (data.status === "success") {
    return { signedUrl: data.data.signed_url };
  } else {
    throw new Error(
      data.message || "An error occurred while fetching tax invoice.",
    );
  }
};

export const useFetchTaxInvoicePDF = (orderId) => {
  const query = useQuery({
    queryKey: ["taxInvoice", orderId],
    queryFn: () => fetchTaxInvoicePDF(orderId),
    enabled: !!orderId,
  });
  return {
    ...query,
    fetchTaxInvoicePDF,
  };
};

const fetchPaymentReceipt = async (paymentId) => {
  const url = new URL(
    `${getOneOmsBaseUrl()}/get-payment-receipt/${paymentId}`,
  );

  const headers = {
    accept: "application/json",
    "secret-key": `${process.env.REACT_APP_ONE_OMS_SECRET_KEY}`,
    "Content-Type": "application/json",
  };

  const response = await fetch(url, {
    method: "GET",
    headers: headers,
  });

  if (!response.ok) {
    const errorData = await response.json();
    throw new Error(
      errorData.message ||
        `Failed to fetch payment receipt. Status: ${response.status}`,
    );
  }

  const data = await response.json();

  if (data.status === "success") {
    return { signedUrl: data.data.signed_url };
  } else {
    throw new Error(
      data.message || "An error occurred while fetching the payment receipt.",
    );
  }
};

export const useFetchPaymentReceipt = (paymentId) => {
  const query = useQuery({
    queryKey: ["paymentReceipt", paymentId],
    queryFn: () => fetchPaymentReceipt(paymentId),
    enabled: !!paymentId,
  });
  return {
    ...query,
    fetchPaymentReceipt,
  };
};
